import Head from 'next/head';
import ReferralType from '@/components/Modules/ReferralType';
import { LoadingSection } from '@/components/LoadingSection';
import { useReferralType } from '@/utils/hooks/useReferralType';
import ReferralTypeInvalid from '@/components/Modules/ReferralTypeInvalid';
import type { ReferAFriendHeroFragment, RImageFragment } from '@/utils/dato/generated';

type HeroBlock = { image?: { responsiveImage?: RImageFragment | null } | null } | undefined | null;

// Every variant's image is already in the page data, but which one is shown is only known once /api/referral-type
// responds. Preloading the few distinct candidates lets the browser fetch the hero while the page hydrates.
const getHeroImages = (blocks: HeroBlock[]) => {
  const images = new Map<string, RImageFragment>();

  blocks.forEach((block) => {
    const responsiveImage = block?.image?.responsiveImage;

    if (responsiveImage) {
      images.set(responsiveImage.src, responsiveImage);
    }
  });

  return Array.from(images.values());
};

const ReferAFriendHeroContent = ({ referralInvalid, referrals }: ReferAFriendHeroFragment) => {
  const { referralType, referCode, isLoading, selected } = useReferralType({ referrals });

  const isPending = referCode === null || isLoading;
  const isInvalid = !referCode || !referralType || !selected;

  if (isPending) {
    return (
      <div className="mt-6">
        <LoadingSection />
      </div>
    );
  }

  if (isInvalid) {
    return <ReferralTypeInvalid {...referralInvalid} />;
  }

  return <ReferralType {...selected} />;
};

const ReferAFriendHero = (props: ReferAFriendHeroFragment) => (
  <>
    <Head>
      {getHeroImages([...props.referrals, props.referralInvalid]).map(({ srcSet, sizes, src }) => (
        <link
          imageSrcSet={srcSet}
          fetchPriority="high"
          imageSizes={sizes}
          rel="preload"
          as="image"
          href={src}
          key={src}
        />
      ))}
    </Head>

    <ReferAFriendHeroContent {...props} />
  </>
);

export default ReferAFriendHero;
